/**
 * CloudPanel — Sidebar panel for managing point clouds in a project.
 * Features: visibility toggle, color change, opacity slider, point size,
 * point shape, RGB/intensity tone mapping, attribute selection, rename.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import type { ZoomTier } from '../../hooks/useSemanticZoom'
import { EtlUploadPanel } from './EtlUploadPanel'

declare global {
  interface Window {
    currentProject: any
    loadedPointClouds: Map<string, { pc: any; config: any }>
    activeCloudIds: string[]
    toggleCloudVisibility: (id: string) => void
    config: any
    setColorFilter: (opts: any) => void
    clearColorFilter: () => void
    _cfState: { enabled: number; color: number[]; threshold: number }
    setClipEdge: (opts: any) => void
    clearClipEdge: () => void
    _ceState: { enabled: number; color: number[]; width: number }
    _navState: { mode: string; enabled: number; floorZ: number | null; eyeHeight: number; radiusIn: number; radiusOut: number; pitchLimitRad: number; debugShowAlways: number; gridSpacing: number; gridRadius: number; teleportMs: number }
    _navEnable: () => void
    _navDisable: () => void
    _navSetParams: (opts: any) => void
  }
}

interface CloudEntry {
  id: string
  name: string
  visible: boolean
  color: string  // hex without #
  opacity: number
  pointCount: string | null
}

const PRESET_COLORS = [
  'ffffff', 'ff4444', 'ff8800', 'ffcc00', '44bb44', '2288ff', '8844ff', 'ff44aa',
]

const POINT_SIZE_TYPES = [
  { value: 0, label: 'Fixed' },
  { value: 1, label: 'Attenuated' },
  { value: 2, label: 'Adaptive' },
]

const POINT_SHAPES = [
  { value: 0, label: 'Square' },
  { value: 1, label: 'Circle' },
  { value: 2, label: 'Paraboloid' },
]

const ATTRIBUTE_OPTIONS = [
  { value: 'rgba', label: 'RGB' },
  { value: 'color', label: 'Farbe (uniform)' },
  { value: 'intensity', label: 'Intensität' },
  { value: 'classification', label: 'Klassifikation' },
  { value: 'elevation', label: 'Höhe' },
  { value: 'return number', label: 'Return Nr.' },
  { value: 'number of returns', label: 'Anzahl Returns' },
  { value: 'source id', label: 'Source ID' },
  { value: 'level of detail', label: 'Detail-Level' },
]

/* ─── Helper: Get material for a cloud id ─── */
function getMat(id: string) {
  return window.loadedPointClouds?.get(id)?.pc?.material ?? null
}

/* ─── Reusable slider row with synced numeric input ─── */
function SliderRow({ label, min, max, step, value, digits, unit, suffix, onChange, onClick }: {
  label: string
  min: number
  max: number
  step: number
  value: number
  digits?: number    // decimal places for display
  unit?: number      // multiply display value (e.g. 100 for %)
  suffix?: string    // e.g. '%'
  onChange: (v: number) => void
  onClick?: (e: React.MouseEvent) => void
}) {
  const sliderRef = useRef<HTMLInputElement>(null)
  const numRef = useRef<HTMLInputElement>(null)
  const u = unit ?? 1
  const d = digits ?? (step < 0.1 ? 2 : step < 1 ? 1 : 0)

  return (
    <div className="ghost-slider">
      <span className="ghost-slider__label">{label}</span>
      <input
        ref={sliderRef}
        type="range" min={min} max={max} step={step}
        defaultValue={String(value)}
        onChange={e => {
          const v = parseFloat(e.target.value)
          onChange(v)
          if (numRef.current) numRef.current.value = (v * u).toFixed(d)
        }}
        onClick={e => { e.stopPropagation(); onClick?.(e) }}
        className="ghost-slider__track"
      />
      <input
        ref={numRef}
        type="number"
        min={min * u} max={max * u} step={step * u}
        defaultValue={(value * u).toFixed(d)}
        onChange={e => {
          let v = parseFloat(e.target.value)
          if (isNaN(v)) return
          v = Math.max(min * u, Math.min(max * u, v))
          onChange(v / u)
          if (sliderRef.current) sliderRef.current.value = String(v / u)
        }}
        onClick={e => e.stopPropagation()}
        className="ghost-field__input ghost-slider__num"
      />
      {suffix && <span className="ghost-slider__value">{suffix}</span>}
    </div>
  )
}

/* ─── Reusable select row ─── */
function SelectRow({ label, options, value, onChange }: {
  label: string
  options: { value: number | string; label: string }[]
  value: number | string
  onChange: (v: string) => void
}) {
  return (
    <div className="ghost-slider">
      <span className="ghost-slider__label">{label}</span>
      <select
        value={String(value)}
        onChange={e => { e.stopPropagation(); onChange(e.target.value) }}
        onClick={e => e.stopPropagation()}
        className="ghost-field__select ghost-field__input"
        style={{ fontSize: '0.625em' }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

/* ─── Section divider ─── */
function SectionLabel({ text }: { text: string }) {
  return (
    <div className="ghost-section__header ghost-section__header--sublabel">
      {text}
    </div>
  )
}

function IconEye({ off }: { off?: boolean }) {
  return off ? (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  )
}

/* ─── Toggle row with label ─── */
function ToggleRow({ label, value, onChange }: {
  label: string
  value: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="ghost-toggle">
      <span className="ghost-toggle__label">{label}</span>
      <button
        onClick={e => { e.stopPropagation(); onChange(!value) }}
        style={{
          width: '32px', height: '16px', borderRadius: '8px', border: 'none',
          background: value ? '#1a1a1a' : '#e5e7eb',
          position: 'relative', cursor: 'pointer', padding: 0,
          transition: 'background 0.15s ease',
        }}
      >
        <div style={{
          width: '12px', height: '12px', borderRadius: '50%',
          background: '#fff', position: 'absolute', top: '2px',
          left: value ? '18px' : '2px',
          transition: 'left 0.15s ease',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }} />
      </button>
    </div>
  )
}

const BACKGROUND_OPTIONS = [
  { value: 'white', label: 'Weiß' },
  { value: 'black', label: 'Schwarz' },
  { value: 'skybox', label: 'Skybox' },
  { value: 'gradient', label: 'Verlauf' },
  { value: 'none', label: 'Transparent' },
]

/* ─── Color Filter section (inside Renderer settings) ─── */
function ColorFilterSection({ tick }: { tick: React.Dispatch<React.SetStateAction<number>> }) {
  const [, forceUpdate] = useState(0)
  const rerender = useCallback(() => forceUpdate(n => n + 1), [])

  // Listen for state changes from script.js
  useEffect(() => {
    window.addEventListener('cf-state-changed', rerender)
    return () => window.removeEventListener('cf-state-changed', rerender)
  }, [rerender])

  // Read directly from global state on every render — no local copies
  const w = window as any
  const st = w._cfState || { enabled: 0, color: [0, 0, 0], threshold: 0.15 }
  const filterActive = !!w._cfFilterActive
  const pickMode = !!w._cfPickMode
  const filterVisible = st.enabled === 1
  const threshold = Math.round((st.threshold ?? 0.15) * 100)

  let filterColor: string | null = null
  if (filterActive && st.color) {
    const [r, g, b] = st.color as number[]
    filterColor = '#' + [r, g, b].map(c => Math.round(c * 255).toString(16).padStart(2, '0')).join('')
  }

  const handleClick = useCallback(() => {
    console.log('[CF-React] Button clicked. pickMode=', !!((window as any)._cfPickMode),
      'filterActive=', !!((window as any)._cfFilterActive))
    const w2 = window as any
    if (w2._cfPickMode) {
      if (w2._cfExitPickMode) w2._cfExitPickMode()
    } else if (w2._cfFilterActive) {
      if (w2._cfDeactivateFilter) w2._cfDeactivateFilter()
    } else {
      if (w2._cfEnterPickMode) w2._cfEnterPickMode()
    }
  }, [])

  return (
    <>
      <SectionLabel text="Farbfilter" />

      <div className="ghost-toggle">
        <span className="ghost-toggle__label">Aktion</span>
        <button
          onPointerDown={e => {
            e.stopPropagation()
            handleClick()
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '4px 8px', border: '1px solid #ddd',
            borderRadius: '6px', cursor: 'pointer', fontSize: '11px',
            background: pickMode ? 'rgba(99,102,241,0.25)' : filterActive ? 'rgba(99,102,241,0.15)' : '#f5f5f5',
            color: '#333',
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
            <circle cx="12" cy="12" r="10"/><path d="m4.9 4.9 14.2 14.2"/>
          </svg>
          {pickMode ? 'Klicke auf Punkt…' : filterActive ? 'Filter aktiv' : 'Farbe wählen'}
          {filterColor && (
            <span style={{
              display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%',
              background: filterColor, border: '1px solid rgba(255,255,255,0.5)', marginLeft: 'auto',
            }} />
          )}
        </button>
      </div>

      {filterActive && (
        <>
          <ToggleRow
            label="Sichtbar"
            value={filterVisible}
            onChange={val => {
              if (window.setColorFilter) window.setColorFilter({ enabled: val })
            }}
          />
          <SliderRow
            label="Toleranz"
            min={1} max={80} step={1}
            value={threshold}
            digits={0} suffix="%"
            onChange={val => {
              const t = Math.round(val) / 100
              if (window.setColorFilter) window.setColorFilter({ threshold: t })
            }}
          />
        </>
      )}
    </>
  )
}

/* ─── Clip Edge Coloring section ─── */
const CE_PRESETS = [
  { hex: '#ff4444', rgb: [1, 0.267, 0.267] },
  { hex: '#ff8800', rgb: [1, 0.533, 0] },
  { hex: '#ffcc00', rgb: [1, 0.8, 0] },
  { hex: '#44bb44', rgb: [0.267, 0.733, 0.267] },
  { hex: '#2288ff', rgb: [0.133, 0.533, 1] },
  { hex: '#8844ff', rgb: [0.533, 0.267, 1] },
  { hex: '#ff44aa', rgb: [1, 0.267, 0.667] },
  { hex: '#ffffff', rgb: [1, 1, 1] },
]

function ClipEdgeSection() {
  const [, forceUpdate] = useState(0)
  const rerender = useCallback(() => forceUpdate(n => n + 1), [])

  useEffect(() => {
    window.addEventListener('ce-state-changed', rerender)
    return () => window.removeEventListener('ce-state-changed', rerender)
  }, [rerender])

  const w = window as any
  const ce = w._ceState || { enabled: 0, color: [1, 0, 0], width: 0.05 }
  const enabled = ce.enabled === 1
  const widthCm = Math.round((ce.width ?? 0.05) * 100)  // Meter → cm
  const activeHex = '#' + ce.color.map((c: number) => Math.round(c * 255).toString(16).padStart(2, '0')).join('')

  return (
    <>
      <SectionLabel text="Schnittkanten" />

      <ToggleRow
        label="Kanten einfärben"
        value={enabled}
        onChange={val => {
          if (window.setClipEdge) window.setClipEdge({ enabled: val })
        }}
      />

      {enabled && (
        <>
          <div className="ghost-slider" style={{ flexWrap: 'wrap' }}>
            <span className="ghost-slider__label">Farbe</span>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {CE_PRESETS.map(p => (
                <button
                  key={p.hex}
                  onPointerDown={e => {
                    e.stopPropagation()
                    if (window.setClipEdge) window.setClipEdge({ color: p.rgb })
                  }}
                  style={{
                    width: '18px', height: '18px', borderRadius: '50%',
                    background: p.hex, border: activeHex === p.hex ? '2px solid #333' : '1px solid #ccc',
                    cursor: 'pointer', padding: 0, flexShrink: 0,
                  }}
                />
              ))}
            </div>
          </div>

          <SliderRow
            label="Breite"
            min={1} max={100} step={1}
            value={widthCm}
            digits={0} suffix="cm"
            onChange={val => {
              const meters = Math.round(val) / 100  // cm → Meter
              if (window.setClipEdge) window.setClipEdge({ width: meters })
            }}
          />
        </>
      )}
    </>
  )
}

/* ─── Personen-Navigation (Prototyp: 3D Navigation & Semantic Zoom) ─── */
function NavigationSection() {
  const [, forceUpdate] = useState(0)
  const rerender = useCallback(() => forceUpdate(n => n + 1), [])

  useEffect(() => {
    window.addEventListener('nav-state-changed', rerender)
    return () => window.removeEventListener('nav-state-changed', rerender)
  }, [rerender])

  const w = window as any
  const nav = w._navState
  if (!nav) return null  // script.js noch nicht geladen

  const enabled = nav.enabled === 1
  const isPerson = nav.mode === 'person'
  const pitchLimitDeg = Math.round((nav.pitchLimitRad * 180) / Math.PI)

  return (
    <>
      <SectionLabel text="Navigation (Prototyp)" />

      <ToggleRow
        label="Personen-Nav aktiv"
        value={enabled}
        onChange={val => {
          if (val) w._navEnable && w._navEnable()
          else w._navDisable && w._navDisable()
        }}
      />

      {enabled && (
        <>
          <div className="ghost-slider" style={{ fontSize: '11px', color: isPerson ? '#10815f' : '#666' }}>
            <span className="ghost-slider__label">Modus</span>
            <span>{isPerson ? '👤 Person' : '🌐 Übersicht'}</span>
          </div>

          <SliderRow
            label="Augenhöhe"
            min={1.2} max={2.0} step={0.05}
            value={nav.eyeHeight}
            digits={2} suffix="m"
            onChange={val => w._navSetParams && w._navSetParams({ eyeHeight: val })}
          />

          <SliderRow
            label="Schwelle ein"
            min={0.5} max={10} step={0.1}
            value={nav.radiusIn}
            digits={1} suffix="m"
            onChange={val => w._navSetParams && w._navSetParams({ radiusIn: val })}
          />

          <SliderRow
            label="Schwelle aus"
            min={0.6} max={15} step={0.1}
            value={nav.radiusOut}
            digits={1} suffix="m"
            onChange={val => w._navSetParams && w._navSetParams({ radiusOut: val })}
          />

          <SliderRow
            label="Pitch-Limit"
            min={10} max={89} step={1}
            value={pitchLimitDeg}
            digits={0} suffix="°"
            onChange={val => w._navSetParams && w._navSetParams({ pitchLimitDeg: val })}
          />

          <SliderRow
            label="Raster"
            min={0.5} max={5.0} step={0.1}
            value={nav.gridSpacing ?? 1.5}
            digits={1} suffix="m"
            onChange={val => w._navSetParams && w._navSetParams({ gridSpacing: val })}
          />

          <SliderRow
            label="Sichtradius"
            min={3} max={50} step={1}
            value={nav.gridRadius ?? 15}
            digits={0} suffix="m"
            onChange={val => w._navSetParams && w._navSetParams({ gridRadius: val })}
          />
        </>
      )}
    </>
  )
}

/* ─── Renderer settings (viewer-level, not per-cloud) ─── */
function RendererSettings({ tick, onOpenSemanticZoom, tier, zoomOverlayOpen }: { tick: React.Dispatch<React.SetStateAction<number>>; onOpenSemanticZoom?: () => void; tier?: ZoomTier; zoomOverlayOpen?: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const [budgetHighlight, setBudgetHighlight] = useState(false)
  const v = (window as any).viewer

  useEffect(() => {
    const handler = () => {
      setExpanded(true)
      setBudgetHighlight(true)
      setTimeout(() => setBudgetHighlight(false), 2800)
    }
    window.addEventListener('highlight-point-budget', handler)
    return () => window.removeEventListener('highlight-point-budget', handler)
  }, [])

  if (!v) return null

  return (
    <div className="ghost-section" data-section="renderer-settings">
      <div
        onClick={() => setExpanded(!expanded)}
        className="ghost-section__header"
      >
        <span className="ghost-section__header__icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
        </span>
        <span className="ghost-section__header__title">
          Renderer
        </span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          className={`ghost-section__chevron ${expanded ? 'ghost-section__chevron--open' : ''}`}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {expanded && (
        <div className="ghost-section__body" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {/* ── Farbfilter ── */}
          <ColorFilterSection tick={tick} />

          {/* ── Schnittkanten ── */}
          <ClipEdgeSection />

          {/* ── Navigation (Prototyp) ── */}
          <NavigationSection />

          {/* ── Eye-Dome Lighting ── */}
          <SectionLabel text="Eye-Dome Lighting" />

          <ToggleRow
            label="EDL"
            value={v.getEDLEnabled()}
            onChange={val => { v.setEDLEnabled(val); tick(t => t + 1) }}
          />

          <SliderRow
            label="Radius"
            min={0} max={4} step={0.1}
            value={v.getEDLRadius()}
            onChange={val => { v.setEDLRadius(val); tick(t => t + 1) }}
          />

          <SliderRow
            label="Stärke"
            min={0} max={5} step={0.1}
            value={v.getEDLStrength()}
            onChange={val => { v.setEDLStrength(val); tick(t => t + 1) }}
          />

          {/* ── Performance ── */}
          <SectionLabel text="Performance" />

          <div className={budgetHighlight ? 'renderer-budget-highlight' : ''}>
            <SliderRow
              label="Point Budget"
              min={100000} max={20000000} step={100000}
              value={v.getPointBudget()}
              digits={0}
              suffix="k"
              unit={0.001}
              onChange={val => v.setPointBudget(val)}
            />
          </div>

          <ToggleRow
            label="HQ Splats"
            value={!!v.useHQ}
            onChange={val => { v.useHQ = val; tick(t => t + 1) }}
          />

          {/* ── Kamera ── */}
          <SectionLabel text="Kamera" />

          <SliderRow
            label="FOV"
            min={20} max={120} step={1}
            value={v.getFOV()}
            digits={0} suffix="°"
            onChange={val => v.setFOV(val)}
          />

          <SliderRow
            label="Nahebene"
            min={0.001} max={10} step={0.001}
            value={v.scene?.getActiveCamera()?.near ?? 0.1}
            onChange={val => {
              const cam = v.scene?.getActiveCamera()
              if (cam) { cam.near = val; cam.updateProjectionMatrix() }
            }}
          />

          {/* ── Hintergrund ── */}
          <SectionLabel text="Anzeige" />

          <SelectRow
            label="Hintergrund"
            options={BACKGROUND_OPTIONS}
            value={v.getBackground()}
            onChange={val => { v.setBackground(val); tick(t => t + 1) }}
          />

          <SliderRow
            label="Min. Node"
            min={0} max={1000} step={10}
            value={v.getMinNodeSize?.() ?? 0}
            digits={0}
            onChange={val => v.setMinNodeSize?.(val)}
          />

          {/* ── Semantic Zoom ── */}
          {onOpenSemanticZoom && (
            <>
              <SectionLabel text="Semantic Zoom" />
              <button
                className="ghost-btn ghost-btn--block"
                data-tier={tier}
                data-active={String(!!zoomOverlayOpen)}
                onClick={onOpenSemanticZoom}
              >
                <div className="zti__dot" />
                <span style={{ flex: 1, textAlign: 'left' }}>
                  {tier === 'overview' ? 'Übersicht'
                    : tier === 'context' ? 'Kontext'
                    : tier === 'detail' ? 'Detail'
                    : 'Inspektion'}
                </span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export function CloudPanel({ onOpenSemanticZoom, tier, zoomOverlayOpen }: { onOpenSemanticZoom?: () => void; tier?: ZoomTier; zoomOverlayOpen?: boolean } = {}) {
  const [clouds, setClouds] = useState<CloudEntry[]>([])
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  // Force re-render when material props change via dropdowns
  const [, setTick] = useState(0)

  // Sync from global state
  useEffect(() => {
    function sync() {
      const project = window.currentProject
      if (!project) { setClouds([]); return; }

      const entries: CloudEntry[] = project.cloudIds.map((id: string) => {
        const entry = window.loadedPointClouds?.get(id)
        const cloudConfig = window.config?.pointclouds?.find((pc: any) => pc.id === id)
        const pc = entry?.pc
        const isVisible = (window.activeCloudIds || []).includes(id)

        let color = 'ffffff'
        let opacity = 1.0
        if (pc?.material) {
          opacity = pc.material.opacity ?? 1.0
          if (pc.material.activeAttributeName === 'color') {
            const c = pc.material.color
            if (c && typeof c.r === 'number') {
              color = [c.r, c.g, c.b].map((v: number) => Math.round(v * 255).toString(16).padStart(2, '0')).join('')
            }
          }
        }

        return { id, name: cloudConfig?.name || id, visible: isVisible, color, opacity, pointCount: null }
      })

      // Desync-Check: activeCloudIds vs pc.visible
      if (window.loadedPointClouds) {
        for (const [id, entry] of window.loadedPointClouds) {
          const inActive = (window.activeCloudIds || []).includes(id)
          const pcVis = entry?.pc?.visible ?? false
          if (inActive !== pcVis) {
            console.warn(`[CloudPanel.sync] DESYNC: ${id} inActive=${inActive} pc.visible=${pcVis}`)
          }
        }
      }

      setClouds(entries)
    }

    sync()
    const interval = setInterval(sync, 1000)
    return () => clearInterval(interval)
  }, [])

  const handleToggleVisibility = useCallback((id: string) => {
    window.toggleCloudVisibility?.(id)
  }, [])

  const handleOpacityChange = useCallback((id: string, value: number) => {
    const mat = getMat(id)
    if (!mat) return
    mat.opacity = value
    mat.transparent = value < 1.0
    mat.needsUpdate = true
  }, [])

  const handleColorChange = useCallback((id: string, hex: string) => {
    const entry = window.loadedPointClouds?.get(id)
    if (!entry?.pc) return
    const mat = entry.pc.material
    if (hex === 'ffffff') {
      mat.activeAttributeName = 'rgba'
      // _ortoColor bewusst nicht löschen — so kann beim nächsten Attributwechsel
      // auf 'color' die zuletzt gewählte Farbe wiederhergestellt werden.
    } else {
      const r = parseInt(hex.slice(0, 2), 16) / 255
      const g = parseInt(hex.slice(2, 4), 16) / 255
      const b = parseInt(hex.slice(4, 6), 16) / 255
      if (mat.color && typeof mat.color.setRGB === 'function') {
        mat.color.setRGB(r, g, b)
      } else {
        mat.uniforms.uColor.value.setRGB(r, g, b)
      }
      mat.activeAttributeName = 'color'
      // Für die Szenen-Speicherung persistent hinterlegen (Fallback zu mat.color).
      mat._ortoColor = { r, g, b }
    }
  }, [])

  const handleStartRename = useCallback((id: string, currentName: string) => {
    setRenamingId(id)
    setRenameValue(currentName)
  }, [])

  const handleFinishRename = useCallback((id: string) => {
    const newName = renameValue.trim()
    if (newName) {
      const cloudConfig = window.config?.pointclouds?.find((pc: any) => pc.id === id)
      if (cloudConfig) {
        cloudConfig.name = newName
        if ((window as any).saveConfig) (window as any).saveConfig()
      }
    }
    setRenamingId(null)
  }, [renameValue])

  if (clouds.length === 0) {
    return (
      <div className="ghost-empty">
        <div>Kein Projekt geladen</div>
        <div className="ghost-empty__hint">
          Lade ein Projekt über die Command Palette (⌘K)
        </div>
      </div>
    )
  }

  return (
    <div data-component="CloudPanel" className="ghost-list">
      {/* Project name */}
      <div className="ghost-section__header ghost-section__header--project">
        {window.currentProject?.name || 'Projekt'}
        <span className="ghost-section__header__count">
          {clouds.length} Wolke{clouds.length !== 1 ? 'n' : ''}
        </span>
      </div>

      {/* ── Renderer Settings ──────────────────────────────────────── */}
      <RendererSettings tick={setTick} onOpenSemanticZoom={onOpenSemanticZoom} tier={tier} zoomOverlayOpen={zoomOverlayOpen} />

      {/* ── ETL Upload (e57/LAS → potree) ─────────────────────────── */}
      <EtlUploadPanel />

      {/* ── Trenner: Punktwolken ── */}
      <div className="ghost-section__header ghost-section__header--project">
        Punktwolken
      </div>

      {clouds.map(cloud => {
        const isExpanded = expandedId === cloud.id
        const isRenaming = renamingId === cloud.id
        const mat = getMat(cloud.id)

        return (
          <div key={cloud.id} className="ghost-section" data-section="cloud-row" data-cloud-id={cloud.id}>
            {/* Main row */}
            <div
              className="ghost-section__header"
              onClick={() => setExpandedId(isExpanded ? null : cloud.id)}
            >
              {/* Visibility toggle */}
              <button
                data-element="btn-toggle-visibility"
                onClick={e => { e.stopPropagation(); handleToggleVisibility(cloud.id) }}
                title={cloud.visible ? 'Ausblenden' : 'Einblenden'}
                className="ghost-action-btn"
                style={{
                  color: cloud.visible ? '#1a1a1a' : '#9ca3af',
                  opacity: cloud.visible ? 1 : 0.4,
                  width: '1.5em', height: '1.5em',
                }}
              >
                <IconEye off={!cloud.visible} />
              </button>

              {/* Name + Edit button */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '0.25em' }}>
                {isRenaming ? (
                  <input
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onBlur={() => handleFinishRename(cloud.id)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleFinishRename(cloud.id)
                      if (e.key === 'Escape') setRenamingId(null)
                    }}
                    onClick={e => e.stopPropagation()}
                    autoFocus
                    className="ghost-field__input"
                    style={{ fontSize: '0.75em', padding: '0.1667em 0.5em' }}
                  />
                ) : (
                  <>
                    <div className={`ghost-item__title ghost-item__title--cloud${cloud.visible ? '' : ' is-hidden'}`}>
                      {cloud.name}
                    </div>
                    <button
                      data-element="btn-rename"
                      onClick={e => { e.stopPropagation(); handleStartRename(cloud.id, cloud.name) }}
                      title="Umbenennen"
                      className="ghost-action-btn ghost-action-btn--rename"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {/* Expand indicator */}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                className={`ghost-section__chevron ${isExpanded ? 'ghost-section__chevron--open' : ''}`}
                style={{ opacity: 0.4, flexShrink: 0 }}
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>

            {/* Expanded controls */}
            {isExpanded && (
              <div className="ghost-section__body" data-section="cloud-controls" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>

                {/* ── Darstellung ── */}
                <SectionLabel text="Darstellung" />

                {/* Attribut / Einfärbung */}
                <SelectRow
                  label="Attribut"
                  options={ATTRIBUTE_OPTIONS}
                  value={mat?.activeAttributeName ?? 'rgba'}
                  onChange={v => {
                    if (!mat) return
                    mat.activeAttributeName = v
                    setTick(t => t + 1)
                  }}
                />

                {/* Color presets + color picker */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontSize: '9px', color: '#9ca3af', width: '62px', flexShrink: 0 }}>Farbe</span>
                  <div className="ghost-colors">
                    {PRESET_COLORS.map(hex => {
                      const isActive = cloud.color === hex
                      return (
                        <button
                          key={hex}
                          onClick={e => { e.stopPropagation(); handleColorChange(cloud.id, hex) }}
                          title={hex === 'ffffff' ? 'Original (RGB)' : `#${hex}`}
                          className={`ghost-color-swatch ${isActive ? 'ghost-color-swatch--active' : ''}`}
                          style={{
                            backgroundColor: `#${hex}`,
                            border: isActive
                              ? '2px solid #1a1a1a'
                              : hex === 'ffffff'
                                ? '1px solid #e5e7eb'
                                : `1px solid #${hex}88`,
                            boxShadow: isActive
                              ? '0 0 4px rgba(26,26,26,0.5)'
                              : hex === 'ffffff' ? 'inset 0 0 3px rgba(0,0,0,0.1)' : 'none',
                          }}
                        />
                      )
                    })}
                    {/* Native color picker */}
                    <div style={{ position: 'relative', width: '24px', height: '24px', marginLeft: '2px' }}>
                      <input
                        type="color"
                        value={`#${cloud.color}`}
                        onChange={e => {
                          e.stopPropagation()
                          const hex = e.target.value.replace('#', '')
                          handleColorChange(cloud.id, hex)
                        }}
                        onClick={e => e.stopPropagation()}
                        title="Eigene Farbe wählen"
                        style={{
                          position: 'absolute', inset: 0,
                          width: '24px', height: '24px',
                          padding: 0, border: 'none',
                          background: 'transparent', cursor: 'pointer',
                          opacity: 0,
                        }}
                      />
                      <div style={{
                        width: '24px', height: '24px', borderRadius: '50%',
                        background: 'conic-gradient(from 0deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
                        border: '1px solid #e5e7eb',
                        pointerEvents: 'none',
                      }} />
                    </div>
                  </div>
                </div>

                {/* Opacity */}
                <SliderRow
                  label="Deckkraft"
                  min={0} max={1} step={0.05}
                  value={cloud.opacity}
                  unit={100} digits={0} suffix="%"
                  onChange={v => handleOpacityChange(cloud.id, v)}
                />

                {/* ── Punktgröße ── */}
                <SectionLabel text="Punktgröße" />

                <SelectRow
                  label="Typ"
                  options={POINT_SIZE_TYPES}
                  value={mat?.pointSizeType ?? 0}
                  onChange={v => { if (mat) mat.pointSizeType = parseInt(v) }}
                />

                <SliderRow
                  label="Größe"
                  min={0} max={5} step={0.1}
                  value={mat?.size ?? 1}
                  onChange={v => { if (mat) mat.size = v }}
                />

                <SliderRow
                  label="Min. Größe"
                  min={0} max={10} step={0.5}
                  value={mat?.minSize ?? 2}
                  onChange={v => { if (mat) mat.minSize = v }}
                />

                <SliderRow
                  label="Max. Größe"
                  min={1} max={100} step={1}
                  value={mat?.maxSize ?? 50}
                  digits={0}
                  onChange={v => { if (mat) mat.maxSize = v }}
                />

                {/* Point shape */}
                <SelectRow
                  label="Form"
                  options={POINT_SHAPES}
                  value={mat?.shape ?? 0}
                  onChange={v => { if (mat) mat.shape = parseInt(v) }}
                />

                {/* ── RGB Tonemapping ── */}
                <SectionLabel text="RGB" />

                <SliderRow
                  label="Gamma"
                  min={0} max={4} step={0.05}
                  value={mat?.rgbGamma ?? 1}
                  onChange={v => { if (mat) mat.rgbGamma = v }}
                />

                <SliderRow
                  label="Helligkeit"
                  min={-1} max={1} step={0.05}
                  value={mat?.rgbBrightness ?? 0}
                  onChange={v => { if (mat) mat.rgbBrightness = v }}
                />

                <SliderRow
                  label="Kontrast"
                  min={-1} max={1} step={0.05}
                  value={mat?.rgbContrast ?? 0}
                  onChange={v => { if (mat) mat.rgbContrast = v }}
                />

                {/* ── Intensity Tonemapping ── */}
                <SectionLabel text="Intensität" />

                <SliderRow
                  label="Gamma"
                  min={0} max={4} step={0.05}
                  value={mat?.intensityGamma ?? 1}
                  onChange={v => { if (mat) mat.intensityGamma = v }}
                />

                <SliderRow
                  label="Helligkeit"
                  min={-1} max={1} step={0.05}
                  value={mat?.intensityBrightness ?? 0}
                  onChange={v => { if (mat) mat.intensityBrightness = v }}
                />

                <SliderRow
                  label="Kontrast"
                  min={-1} max={1} step={0.05}
                  value={mat?.intensityContrast ?? 0}
                  onChange={v => { if (mat) mat.intensityContrast = v }}
                />

                {/* ── Extra Attribute Tonemapping ── */}
                <SectionLabel text="Extra-Attribut" />

                <SliderRow
                  label="Gamma"
                  min={0} max={4} step={0.05}
                  value={mat?.extraGamma ?? 1}
                  onChange={v => { if (mat) mat.extraGamma = v }}
                />

                <SliderRow
                  label="Helligkeit"
                  min={-1} max={1} step={0.05}
                  value={mat?.extraBrightness ?? 0}
                  onChange={v => { if (mat) mat.extraBrightness = v }}
                />

                <SliderRow
                  label="Kontrast"
                  min={-1} max={1} step={0.05}
                  value={mat?.extraContrast ?? 0}
                  onChange={v => { if (mat) mat.extraContrast = v }}
                />

                {/* ── Gewichtung ── */}
                <SectionLabel text="Gewichtung" />

                <SliderRow
                  label="RGB"
                  min={0} max={1} step={0.05}
                  value={mat?.weightRGB ?? 1}
                  onChange={v => { if (mat) mat.weightRGB = v }}
                />

                <SliderRow
                  label="Intensität"
                  min={0} max={1} step={0.05}
                  value={mat?.weightIntensity ?? 0}
                  onChange={v => { if (mat) mat.weightIntensity = v }}
                />

                <SliderRow
                  label="Höhe"
                  min={0} max={1} step={0.05}
                  value={mat?.weightElevation ?? 0}
                  onChange={v => { if (mat) mat.weightElevation = v }}
                />

                <SliderRow
                  label="Klassifikation"
                  min={0} max={1} step={0.05}
                  value={mat?.weightClassification ?? 0}
                  onChange={v => { if (mat) mat.weightClassification = v }}
                />

                <SliderRow
                  label="Return Nr."
                  min={0} max={1} step={0.05}
                  value={mat?.weightReturnNumber ?? 0}
                  onChange={v => { if (mat) mat.weightReturnNumber = v }}
                />

                <SliderRow
                  label="Source ID"
                  min={0} max={1} step={0.05}
                  value={mat?.weightSourceID ?? 0}
                  onChange={v => { if (mat) mat.weightSourceID = v }}
                />

              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
